import type { AccommodationAvailabilitySettings } from "@@/shared/acquire/accommodation-availability";
import {
    adjustTimeForAccommodationSettings,
    calculateAvailability,
} from "@@/shared/acquire/accommodation-engine/accommodation-helpers";
import type { RatePriceAdjustment } from "@@/shared/acquire/accommodation-engine/rate-price-adjustment";
import {
    getWeekday,
    weekdaysOnDefault,
} from "@@/shared/acquire/accommodation-engine/weekdays-on-off";
import type { AccommodationQuantities } from "@@/shared/acquire/accommodation-quantity";
import type { AccommodationRate } from "@@/shared/acquire/accommodation-rate";
import type { AccommodationRateId } from "@@/shared/acquire/accommodation-rate-id";
import type { AccommodationRateOption } from "@@/shared/acquire/accommodation-rate-option";
import type {
    AccommodationRateOptionApplies,
    AccommodationRateOptionAppliesTo,
} from "@@/shared/acquire/accommodation-rate-option-applies";
import type { AccommodationRateOptionId } from "@@/shared/acquire/accommodation-rate-option-id";
import { isAccommodation } from "@@/shared/acquire/is-accommodation";
import type { ResourceGraph } from "@@/shared/acquire/resources/resource-graph";
import { isResourceGroup } from "@@/shared/acquire/resources/resource-group";
import type { ResourceId } from "@@/shared/acquire/resources/resource-id";
import type { ResourceReservationRequestInfo } from "@@/shared/acquire/resources/resource-reservation-info";
import type { ResourceAvailability } from "@@/shared/acquire/sessions/booking-helpers";
import { findAllGraphs } from "@@/shared/acquire/sessions/flatten-graph";
import { asDate } from "@@/shared/date-time/as-date";
import type { DateRange } from "@@/shared/date-time/date-range";
import { datesBetween } from "@@/shared/date-time/date-utility";
import {
    isoAndUnixFactory,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import { startOfDayZoned } from "@@/shared/date-time/start-and-end-of-day-zoned";
import { timeRangeFactory } from "@@/shared/date-time/time-range";
import { addIncludingVatPrice } from "@@/shared/prices/add-price";
import { priceFactory, type Price } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { Product } from "@@/shared/products/product";
import type { ProductId } from "@@/shared/products/product-id";
import { defaultTimeZone } from "@@/shared/weekly-schedules/default-time-zone";
import { generateId } from "@@/utilities/generate-id";
import { groupBy } from "@@/utilities/group-by";
import { sortBy } from "@@/utilities/sort";
import { sum } from "@@/utilities/sum";
import { addDays, endOfDay, isPast, startOfDay, subMinutes } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { toZonedTime } from "date-fns-tz/toZonedTime";
import type { Except } from "type-fest";

const getDefaultEmptyFeed = (product: Product): AccommodationFeed => {
    return {
        _id: generateId("feed__"),
        _type: "ACCOMMODATION_FEED",
        _createdAt: nowIsoAndUnix(),
        productId: product._id,
        dates: [],
        debug: {
            generatedAt: nowIsoAndUnix(),
            finishedAt: nowIsoAndUnix(),
            duration: "0ms",
        },
    };
};

/**
 * Gets all the date ranges for the minimum stay and maximum stay with the ranges from a specific date
 * @param settings
 * @param date
 * @returns
 */
const _calculateAllMinMaxRanges = (
    settings: AccommodationAvailabilitySettings,
    date: Date,
) => {
    if (settings.minimumStay === settings.maximumStay) {
        return [
            {
                range: timeRangeFactory(
                    date,
                    addDays(date, settings.minimumStay),
                ),
                minStay: settings.minimumStay,
                maxStay: settings.maximumStay,
            },
        ];
    }

    const days = settings.maximumStay - settings.minimumStay;
    const allRanges = Array.from({ length: days + 1 }, (_, i) => {
        const index = i;
        const start = date;
        const end = addDays(date, settings.minimumStay + index);
        const newRange = timeRangeFactory(start, end);
        const range = adjustTimeForAccommodationSettings(newRange, settings);
        return {
            range,
            minStay: settings.minimumStay,
            maxStay: settings.minimumStay + index,
        };
    });
    return allRanges;
};

const accommodationFeedFactory = (
    product: Product,
    currentReservations: ResourceReservationRequestInfo[],
    globalResources: ResourceGraph[],
    adjustments: RatePriceAdjustment[],
    options?: {
        overrideTimeAvailability?: boolean; //If true, override checks for if the checkin time has passed
        generateWithRange?: DateRange; //If set, generate feed with this range to speed up the process or generate dates when the accommodation is not available for channex
        limitEndRangeToMatchAvailability?: boolean; //If true, limit the end range to the last availability date if generateWithRange is set
    },
): AccommodationFeed => {
    //1. Calculate All dates
    //2. Calculate for all dates
    //3. Calculate rate/price for all dates
    const start = nowIsoAndUnix();
    const accommodation = product.skus[0].acquire;

    if (!isAccommodation(accommodation))
        throw new Error("Not an accommodation");

    if (accommodation.availability.availabilityRanges.length === 0) {
        return {
            _id: generateId("feed__"),
            _type: "ACCOMMODATION_FEED",
            _createdAt: nowIsoAndUnix(),
            productId: product._id,
            dates: [],
            debug: {
                generatedAt: start,
                finishedAt: nowIsoAndUnix(),
                duration:
                    (
                        new Date().getTime() - asDate(start).getTime()
                    ).toString() + "ms",
            },
        } as AccommodationFeed;
    }

    const allSpans = accommodation.availability.availabilityRanges
        .flatMap(firstDate => {
            const start = firstDate.start;
            const end = firstDate.end;
            return [start, end];
        })
        .sort(sortBy(r => r.unix));
    //
    const generateWithRange = options?.generateWithRange;

    const firstDate = startOfDay(
        asDate(generateWithRange?.start ?? allSpans[0]),
    );
    const lastDate = endOfDay(
        asDate(
            options?.generateWithRange &&
                !options.limitEndRangeToMatchAvailability
                ? options?.generateWithRange.end
                : allSpans[allSpans.length - 1],
        ),
    );

    const allDates = datesBetween(firstDate, lastDate);
    // Get the current timezone
    const currentTz =
        Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ||
        "Europe/Stockholm";

    const groupedAdjustments = groupBy(adjustments, a => a.target);

    const all = globalResources.flatMap(r => findAllGraphs(r));

    const allGroups = all.flatMap(r => {
        const group = r.resourceGroup;
        if (isResourceGroup(group)) return r;
        else return [];
    });

    const feed = allDates.map((date2, _i) => {
        // Get the current date in UTC from
        const date = fromZonedTime(date2, currentTz);

        //Todo refactor this to a function and make it more readable and timezonable
        const availabilityRange =
            accommodation.availability.availabilityRanges.find(
                r =>
                    date >= startOfDay(asDate(r.start)) &&
                    date <= endOfDay(asDate(r.end)),
            );

        if (!availabilityRange)
            return {
                date: isoAndUnixFactory(date),
                isAvailable: false,
                settings: {
                    canCheckIn: false,
                    canCheckOut: false,
                    minimumStay: 0,
                    maximumStay: 0,
                    checkInTime: {
                        hour: 0,
                        minute: 0,
                    },
                    checkOutTime: {
                        hour: 0,
                        minute: 0,
                    },
                },
                accommodations: [],
            } as AccommodationAvailabilityDate;

        const settings =
            availabilityRange?.settings ??
            accommodation.availability.baseSettings;

        const end = addDays(date, 1);

        const range1 = timeRangeFactory(date, end);
        const minRange = adjustTimeForAccommodationSettings(range1, settings);

        //Calculate all min stay and max stay date ranges
        // const allRanges = _calculateAllMinMaxRanges(settings, date);

        //Check that the date is correct
        const weekday = getWeekday(toZonedTime(date, defaultTimeZone));

        const resourceAvailability = allGroups.map(r => {
            const calculatedAvailability = calculateAvailability({
                timeRange: minRange,
                quantity: 1,
                reservations: currentReservations,
                resourceIds: [r._id],
                globalResources: [r],
                settings,
                options: {
                    overrideSession: !!options?.overrideTimeAvailability,
                },
            });

            // Used to check if the resource can be booked used to test the longest range to be able to book
            // const canBeBooked = (t: TimeRange) => {
            //     return calculateAvailability({
            //         timeRange: t,
            //         quantity: 1,
            //         reservations: currentReservations,
            //         resourceIds: [r._id],
            //         globalResources: [r],
            //         settings,
            //         options: {
            //             overrideSession: !!options?.overrideTimeAvailability,
            //         },
            //     }).resourcesAvailability.some(ra => ra.available);
            // };
            //Find the min and max stay range that can be booked
            // const _longestRange = allRanges.findLast(r => canBeBooked(r.range));

            const specificRates = accommodation.rates.filter(rate =>
                rate.appliesTo.some(ra => ra === r._id),
            );

            //Check parents rates

            const parentId = r.parentId;
            const parentRates = accommodation.rates.filter(rate =>
                rate.appliesTo.some(ra => ra === parentId),
            );

            const rates =
                specificRates.length > 0 ? specificRates : parentRates;

            //** TODO lookover startOfDayZoned, I think it just resets it to UTC currently */
            const dateAsIsoAndUnix = isoAndUnixFactory(startOfDayZoned(date));

            const result: AccommodationAvailabilityAndRates = {
                _type: "ACCOMMODATION_AVAILABILITY_AND_RATES",
                date: dateAsIsoAndUnix,
                resourceGroupId: r._id,

                minStay: settings.minimumStay, //longestRange?.minStay ?? 0, // settings.minimumStay,
                maxStay: settings.maximumStay, //longestRange?.maxStay ?? 0, //settings.maximumStay,

                checkInAllowed: settings.checkIn
                    ? settings.checkIn[weekday]
                    : true,
                checkOutAllowed: settings.checkOut
                    ? settings.checkOut[weekday]
                    : true,

                resourceAvailability:
                    calculatedAvailability.resourcesAvailability,
                calculatedRates: rates.map(rate => {
                    const optionPrices = rate.options.map(option => {
                        //Find adjustments for the rate and applyThem
                        const adjustments =
                            groupedAdjustments.get(option._id) ?? [];

                        const activeAdjustments = adjustments.filter(
                            adjustment => {
                                // First, if the date is not within the adjustment date range
                                // we should not apply the adjustment
                                if (
                                    adjustment.range.start.unix >
                                        dateAsIsoAndUnix.unix ||
                                    (adjustment.range.end.unix <
                                        dateAsIsoAndUnix.unix &&
                                        adjustment.range.start.unix !==
                                            adjustment.range.end.unix)
                                ) {
                                    return false;
                                }

                                // Make sure the adjustment isn't turned of the specific date
                                if (
                                    adjustment.inactiveDates?.some(
                                        deviation =>
                                            deviation.slice(0, 10) ===
                                            dateAsIsoAndUnix.iso.slice(0, 10),
                                    )
                                )
                                    return false;

                                // So, the date is within the adjustment date range
                                // check if the adjustment is active the weekday...
                                if (
                                    adjustment.weekdaysOn &&
                                    adjustment.weekdaysOn[weekday] === false
                                )
                                    return false;

                                // ...and date in question
                                return (
                                    adjustment.range.start.unix <=
                                        dateAsIsoAndUnix.unix &&
                                    adjustment.range.end.unix >=
                                        dateAsIsoAndUnix.unix
                                );
                            },
                        );

                        const applyAdjustments = activeAdjustments.reduce(
                            (price, adjustment) => {
                                if (adjustment.adjustment.type === "AMOUNT") {
                                    return addIncludingVatPrice(
                                        price,
                                        adjustment.adjustment.value,
                                    );
                                }

                                return priceFactory({
                                    amountIncludingVat:
                                        price.amountIncludingVat *
                                        adjustment.adjustment.value,
                                    vat: price.vat,
                                });
                            },
                            option.price,
                        );
                        //Todo should we store that the price has been adjusted?
                        const result =
                            accommodationRateOptionForDateWithCalculatedPricesFactory(
                                {
                                    rateId: rate._id,
                                    rateOption: option,
                                    calculatedPrices: {
                                        price: applyAdjustments,
                                        basePrice: option.price,
                                        adjustments: activeAdjustments.map(
                                            a => a.adjustment,
                                        ),
                                    },
                                },
                            );

                        return result;
                    });

                    const result =
                        accommodationRateForDateWithCalculatedPricesFactory(
                            rate,

                            optionPrices,
                        );
                    return result;
                }),
            };
            return result;
        });

        const advanceNotice = settings.settings.advanceNotice;

        const startTime = settings.settings.allowBookingDuringSession
            ? asDate(minRange.end)
            : asDate(minRange.start);

        const isResourceAvailable = resourceAvailability.some(ra =>
            ra.resourceAvailability.some(r => r.available),
        );

        const canCheckIn = () => {
            const checkIn = settings.checkIn ?? weekdaysOnDefault;

            if (!checkIn[weekday]) return false;

            if (!isResourceAvailable) return false;

            if (options?.overrideTimeAvailability) return true;

            return !isPast(subMinutes(startTime, advanceNotice));
        };

        const canCheckOut = () => {
            const checkOut = settings.checkOut ?? weekdaysOnDefault;

            return checkOut[weekday] ?? false;
        };

        return {
            _type: "ACCOMMODATION_AVAILABILITY_ON_DATE",
            date: isoAndUnixFactory(startOfDayZoned(date)), //isoAndUnixFactory(date),

            isAvailable: isResourceAvailable,
            settings: {
                canCheckIn: canCheckIn(),
                //: true, //If we override the availability, we can check in anytime
                canCheckOut: canCheckOut(), //true,
                minimumStay: settings.minimumStay,
                maximumStay: settings.maximumStay,
                checkInTime: settings.checkInTime,
                checkOutTime: settings.checkOutTime,
            },
            accommodations: resourceAvailability,
        } as AccommodationAvailabilityDate;
    });

    return {
        _id: generateId("feed__"),
        _type: "ACCOMMODATION_FEED",
        _createdAt: nowIsoAndUnix(),
        productId: product._id,
        dates: feed,
        debug: {
            generatedAt: start,
            finishedAt: nowIsoAndUnix(),
            duration:
                (new Date().getTime() - asDate(start).getTime()).toString() +
                "ms",
        },
    } as AccommodationFeed;
};
type AccommodationFeed = {
    _id: string;
    _type: "ACCOMMODATION_FEED";
    _createdAt: IsoAndUnixTimestamp;
    productId: ProductId;
    dates: AccommodationAvailabilityDate[];
    debug?: {
        generatedAt: IsoAndUnixTimestamp;
        finishedAt: IsoAndUnixTimestamp;
        duration: string;
    };
};

type AccommodationAvailabilityDate = {
    date: IsoAndUnixTimestamp;
    isAvailable: boolean;
    settings: {
        canCheckIn: boolean;
        canCheckOut: boolean;
        minimumStay: number;
        maximumStay: number;
        checkInTime: {
            hour: number;
            minute: number;
        };
        checkOutTime: {
            hour: number;
            minute: number;
        };
    };

    accommodations: AccommodationAvailabilityAndRates[];
};

type AccommodationRateOptionForDateWithCalculatedPrices = Except<
    AccommodationRateOption,
    "_type" | "_id"
> & {
    _type: "ACCOMMODATION_RATE_OPTION_FOR_DATE_WITH_CALCULATED_PRICES";
    date: IsoAndUnixTimestamp;
    rateId: AccommodationRateId;
    rateOptionId: AccommodationRateOptionId;
    applies: AccommodationRateOptionApplies;
    appliesTo: AccommodationRateOptionAppliesTo;
    price: Price;
    basePrice: Price;
    adjustments: RatePriceAdjustment["adjustment"][];
    adjustmentValue: number;
};
const accommodationRateOptionForDateWithCalculatedPricesFactory = (params: {
    rateId: AccommodationRateId;
    rateOption: AccommodationRateOption;
    calculatedPrices: {
        price: Price;
        basePrice: Price;
        adjustments: RatePriceAdjustment["adjustment"][];
    };
}): AccommodationRateOptionForDateWithCalculatedPrices => {
    const { rateOption, calculatedPrices, rateId } = params;
    const { _id, _type, ...passOn } = rateOption;
    return {
        ...passOn,
        _type: "ACCOMMODATION_RATE_OPTION_FOR_DATE_WITH_CALCULATED_PRICES",
        date: nowIsoAndUnix(),
        rateId,
        rateOptionId: rateOption._id,
        price: calculatedPrices.price,
        basePrice: calculatedPrices.basePrice,
        adjustments: calculatedPrices.adjustments,
        adjustmentValue: sum(calculatedPrices.adjustments.map(a => a.value)),
    };
};

type AccommodationRateForDateWithCalculatedPrices = Except<
    AccommodationRate,
    "_type" | "_id"
> & {
    _type: "ACCOMMODATION_RATE_FOR_DATE_WITH_CALCULATED_PRICES";
    date: IsoAndUnixTimestamp;
    rateId: AccommodationRateId;
    price: Price;
    basePrice: Price;
    adjustments: RatePriceAdjustment["adjustment"][];
    adjustmentValue: number;
    rateOptionPrices: AccommodationRateOptionForDateWithCalculatedPrices[];
};

const accommodationRateForDateWithCalculatedPricesFactory = (
    rate: AccommodationRate,
    rateOptionPrices: AccommodationRateOptionForDateWithCalculatedPrices[],
): AccommodationRateForDateWithCalculatedPrices => {
    const { _id, _type, ...passOn } = rate;

    return {
        ...passOn,
        _type: "ACCOMMODATION_RATE_FOR_DATE_WITH_CALCULATED_PRICES",
        date: nowIsoAndUnix(),
        rateId: rate._id,
        price: sumPrices(rateOptionPrices.map(item => item.price)),
        basePrice: sumPrices(rateOptionPrices.map(item => item.basePrice)),
        adjustments: rateOptionPrices.flatMap(item => item.adjustments),
        adjustmentValue: sum(
            rateOptionPrices.flatMap(item =>
                item.adjustments.map(a => a.value),
            ),
        ),
        rateOptionPrices,
    };
};

type AccommodationAvailabilityAndRates = {
    _type: "ACCOMMODATION_AVAILABILITY_AND_RATES";
    date: IsoAndUnixTimestamp;
    minStay: number;
    maxStay: number;

    checkInAllowed: boolean;
    checkOutAllowed: boolean;

    resourceGroupId: ResourceId;
    resourceAvailability: ResourceAvailability[];
    calculatedRates: AccommodationRateForDateWithCalculatedPrices[]; //Todo adjust with another type
};
type CalculatedRateLineItem = {
    quantity: number;
    pricePerItem: Price;

    totalPrice: Price;
    option: AccommodationRateOptionForDateWithCalculatedPrices;
    night: IsoAndUnixTimestamp;
};

type CalculatedRatePrice = {
    quantities: AccommodationQuantities;
    rateWithPrices: AccommodationRateForDateWithCalculatedPrices;
    price: Price;
    rateLineItems: CalculatedRateLineItem[];
};

export {
    accommodationFeedFactory,
    accommodationRateForDateWithCalculatedPricesFactory,
    accommodationRateOptionForDateWithCalculatedPricesFactory,
    getDefaultEmptyFeed,
};
export type {
    AccommodationAvailabilityAndRates,
    AccommodationAvailabilityDate,
    AccommodationFeed,
    AccommodationRateForDateWithCalculatedPrices,
    AccommodationRateOptionForDateWithCalculatedPrices,
    CalculatedRateLineItem,
    CalculatedRatePrice,
};
